import { DocumentTypes, Layer } from 'types/inspection';
import { PendingFile, UploadedFile, FailedFile } from 'store/request/files/types';
import { FileErrors, type FileMergeMetadata as MergedFileData, type PdfBox } from '@verify/shared-utils';

export interface InspectFileState {
  fileId: string;
  url: string;
  fileName: string;
  originalName: string;
  status: FileStatus | string;
  createdAt: string;
  mergedFileData?: MergedFileData[];
  isOCR?: boolean;
  fileMetadata?: FileMetadata;
}

export interface PendingReorderData {
  pendingFiles: PendingFile[];
  documentType: DocumentTypes;
  removeHeadersFooters: boolean;
}

export interface ReadyFilesQueueItem {
  uploadedFiles: UploadedFile[];
  failedFiles: FailedFile[];
}

export interface PendingIdUpdate {
  tempId: string;
  realId: string;
}

export interface PendingFileMapping {
  tempId: string;
  filename: string;
}

export interface FailedUploadNotification {
  fileIds: string[];
  filenames: string[];
}

export interface FilesState {
  source: {
    file: InspectFileState;
    fileUnavailable: boolean;
  };
  target: {
    file: InspectFileState;
    fileUnavailable: boolean;
  };
  filesList: Array<InspectFileState> | null;
  isFetchingInspectionFiles: boolean;
  fileErrorType?: FileErrors;
  requestKeys: { source: string; target: string };
  multipleFileStatus: { source: MultipleFileStatus; target: MultipleFileStatus };
  ocrStatus: { source: FileStatus | ''; target: FileStatus | '' };
  shapesJobStatus: FileStatus | '';
  ocrHasRun: boolean;
  isCreationCancelled: boolean;
  activeFileTransferIds: {
    [DocumentTypes.source]: string | null;
    [DocumentTypes.target]: string | null;
  };
  pendingReorderData: PendingReorderData | null;
  readyFilesQueue: ReadyFilesQueueItem[];
  pendingIdUpdates: PendingIdUpdate[];
  failedUploads: FailedUploadNotification[];
  // Track cancelled file IDs so we can filter them out when their batch finishes
  cancelledFileIds: string[];
  // Track cancelled filenames (for files cancelled before they get real IDs)
  cancelledFilenames: string[];
  // Track which batch each file belongs to (fileId -> batchKey)
  fileToBatchMap: { [fileId: string]: string };
  // Track which batches are fully cancelled (all files cancelled)
  cancelledBatchKeys: string[];
}

export type MultipleFileStatus = 'UPLOADING' | 'MERGING' | false;

export interface FilesIdData {
  masterFileId: string;
  sampleFileId: string;
}

export enum FileStatus {
  completed = 'COMPLETE',
  queued = 'QUEUED',
  error = 'ERROR',
  inprogress = 'IN_PROGRESS',
  pending = 'PENDING',
  cancelled = 'CANCELLED',
}

export interface NewMergedData {
  filenumber: number;
  name: string;
}

export interface FileMetadata {
  checksum?: string;
  pdfBoxes?: {
    ArtBox?: PdfBox[];
    CropBox?: PdfBox[];
    BleedBox?: PdfBox[];
    TrimBox?: PdfBox[];
  };
  ctfVersion?: string;
  containsType3Font?: boolean;
  allLayers?: Layer[];
  allSeparations?: string[];
  layersVisibility?: Layer[];
  separationsToHide?: string[];
  selectedLayerName?: string;
  selectedSeparationName?: string;
  autoSelectedShapes?: [number, number][][][];
  recommendedBoxType?: {
    name: string;
    regions: PdfBox[];
  };
  pagesBasePath?: string;
}
